/**
 * Macro Recording Take — persistence unit.
 *
 * Redis JSON document, full-replace + rev CAS.
 * Keys: `realtime:campaign:macro-recording:{campaignId}:{takeId}`
 * Index Set: `realtime:campaign:macro-recordings:{campaignId}`
 */

import { z } from 'zod';
import { macroRegionSchema } from './macroTree';
import { gamepadMacroStateSchema } from './gamepadMacro';
import { macroInputDeviceSettingsSchema } from './macroInputDevices';
import { calibrationOutcomeSchema } from './macroCalibration/common';
import { macroCalibrationEvidenceSchema } from './macroCalibration/evidence';

export const macroTemporalWindowSchema = z.object({
  evidence: macroCalibrationEvidenceSchema.optional(),
  calibrationKey: z.string().optional(),
  beforeMs: z.number().int().min(0).max(250),
  afterMs: z.number().int().min(0).max(250),
  sampleIntervalMs: z.union([z.literal(50), z.literal(100)]),
  frames: z.array(z.object({
    id: z.string().uuid(), imageKey: z.string().min(1),
    offsetMs: z.number().int().min(-250).max(250),
    width: z.number().int().positive().max(16_384), height: z.number().int().positive().max(16_384),
    calibration: calibrationOutcomeSchema.optional(),
  })).min(1).max(8),
}).superRefine((window, context) => {
  if (new Set(window.frames.map(frame => frame.id)).size !== window.frames.length)
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['frames'], message: 'Temporal frame IDs must be unique' });
  if (window.frames.filter(frame => frame.offsetMs === 0).length !== 1)
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['frames'], message: 'Keep exactly one selected frame at offset zero' });
});
export type MacroTemporalWindow = z.infer<typeof macroTemporalWindowSchema>;

// ─── Input payload (discriminated by device) ────────────────────────────────

/** Intermediate drag sample: delta since previous waypoint, ms since gesture start. */
export const macroRecordingDragWaypointSchema = z.object({
  dx: z.number(),
  dy: z.number(),
  atMs: z.number(),
});

const dragPayloadFields = {
  startX: z.number(),
  startY: z.number(),
  endX: z.number(),
  endY: z.number(),
  totalDx: z.number(),
  totalDy: z.number(),
  durationMs: z.number(),
  waypoints: z.array(macroRecordingDragWaypointSchema),
} as const;

// NOTE: z.union, not z.discriminatedUnion — zod rejects duplicate discriminator
// values, and 'mouse'/'touch' each appear twice (standard actions + drag).
export const macroRecordingInputPayloadSchema = z.union([
  z.object({
    device: z.literal('keyboard'),
    keyCode: z.number().int(),
    location: z.number().int(),
    modifiers: z.object({
      shift: z.boolean(),
      ctrl: z.boolean(),
      alt: z.boolean(),
    }),
  }),
  z.object({ device: z.literal('gamepad'), deviceId: z.number().int().min(0).max(3).optional(), state: gamepadMacroStateSchema }),
  z.object({ device: z.literal('gamepad'), deviceId: z.number().int().min(0).max(3).optional(), button: z.string() }),
  z
    .object({
      device: z.literal('mouse'),
      action: z.enum(['down', 'up']),
      button: z.enum(['left', 'right', 'middle', 'back', 'forward']).optional(),
      dx: z.number().optional(),
      dy: z.number().optional(),
      x: z.number().min(0).max(1).optional(),
      y: z.number().min(0).max(1).optional(),
    })
    .superRefine((val, ctx) => {
      const hasX = val.x !== undefined;
      const hasY = val.y !== undefined;
      if (hasX !== hasY) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'x and y must both be present or both omitted',
        });
      }
    }),
  z.object({
    device: z.literal('mouse'),
    action: z.literal('move'),
    button: z.enum(['left', 'right', 'middle', 'back', 'forward']).optional(),
    dx: z.number().optional(),
    dy: z.number().optional(),
  }),
  z.object({
    device: z.literal('mouse'), action: z.literal('wheel'),
    dx: z.number().finite().min(-32767).max(32767).optional(),
    dy: z.number().finite().min(-32767).max(32767).optional(),
  }),
  z.object({
    device: z.literal('mouse'),
    action: z.literal('drag'),
    ...dragPayloadFields,
  }),
  z.object({
    device: z.literal('touch'),
    action: z.enum(['down', 'move', 'up']),
    touchId: z.number().int().min(0).max(255).optional(),
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
  }),
  z.object({
    device: z.literal('touch'),
    action: z.literal('drag'),
    touchId: z.number().int().min(0).max(255).optional(),
    startX: z.number().min(0).max(1),
    startY: z.number().min(0).max(1),
    endX: z.number().min(0).max(1),
    endY: z.number().min(0).max(1),
    totalDx: z.number(),
    totalDy: z.number(),
    durationMs: z.number(),
    waypoints: z.array(macroRecordingDragWaypointSchema),
  }),
]);

export type MacroRecordingInputPayload = z.infer<typeof macroRecordingInputPayloadSchema>;

// ─── Take document ──────────────────────────────────────────────────────────

export const macroRecordingTakeSchema = z.object({
  takeId: z.string().uuid(),
  campaignId: z.string().min(1),
  name: z.string().min(1).max(80),
  status: z.enum(['recording', 'finalized', 'published']),
  /** Legacy pointer-emulation metadata; inputDevices controls capture/playback. */
  mouseMode: z.enum(['mouse', 'touch']),
  /** Missing on legacy takes: all seven devices are enabled. */
  inputDevices: macroInputDeviceSettingsSchema.optional(),
  createdAt: z.number(),
  updatedAt: z.number(),
  rev: z.number().int(),
  /**
   * video.key is set by realtime-api on HOST_MACRO_RECORDING_UPLOAD_COMPLETE;
   * durationMs/width/height are backfilled by the editor from the <video>
   * element's loaded metadata (Phase 5) — no native/WS message carries them.
   * startTimestampMs stays optional: alignment uses per-mark native timestampMs
   * + videoTimebaseOffsetMs, not the MP4 wall-clock.
   */
  video: z.object({
    key: z.string(),
    durationMs: z.number(),
    width: z.number(),
    height: z.number(),
    startTimestampMs: z.number().optional(),
  }).nullable(),
  videoTimebaseOffsetMs: z.number().optional(),
  timelineInputs: z.array(z.object({
    id: z.string().uuid(),
    atMs: z.number(),
    kind: z.enum(['tap', 'chord', 'hold', 'drag']),
    durMs: z.number().optional(),
    label: z.string(),
    payload: macroRecordingInputPayloadSchema,
  })),
  timelineScreenStates: z.array(z.object({
    id: z.string().uuid(),
    name: z.string(),
    atMs: z.number(),
    customName: z.boolean().optional(),
    index: z.number().int().optional(),
    timestampMs: z.number().optional(),
    width: z.number().optional(),
    height: z.number().optional(),
    imageKey: z.string().optional(),
    /** 480p preview for the editor. */
    previewUrl: z.string().url().optional(),
    /** Native-resolution PNG; becomes the tree snapshot's `sourceUrl` on publish. */
    sourceUrl: z.string().url().optional(),
    region: macroRegionSchema.optional(),
    temporal: macroTemporalWindowSchema.optional(),
  })),
  publishedAtMs: z.number().optional(),
});

export type MacroRecordingTake = z.infer<typeof macroRecordingTakeSchema>;
